/**
 * Lab Detail tabs for CM Member 360.
 * Soft-empty tables when CM-API analytics/bets/userLog proxies are unavailable.
 */
import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  Chip,
  DataTable,
  DEFAULT_PAGE_SIZE,
  Field,
  FilterBar,
  Input,
  Kpi,
  Legend,
  Modal,
  Pager,
  Popover,
  Segmented,
  Select,
  Skeleton,
  Spinner,
  StateBlock,
  Textarea,
  useToast,
  type ActiveFilter,
  type Column,
} from '@w7/ui'
import { CmApiHttpError, cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { useMemberActivityMatrix } from '@/api/hooks/member/useMemberActivityMatrix'
import { useMemberBets } from '@/api/hooks/member/useMemberBets'
import { useEventsForPll } from '@/api/hooks/reports/useEventsForPll'
import { useGames } from '@/api/hooks/reports/useGames'
import { useSubGames } from '@/api/hooks/reports/useSubGames'
import {
  useDeleteMemberBlockedDeviceId,
  useLoginHistory,
  useSaveMemberBlockedDeviceId,
  type LoginHistoryRow,
} from '@/api/hooks/member/useLoginHistory'
import { useMemberTransfers } from '@/api/hooks/member/useMemberTransfers'
import { useFancyBookmakerCommission } from '@/api/hooks/reports/useFancyBookmakerCommission'
import { labMemberCommissionDisplay } from '@/api/map/fancyBookmakerCommission'
import {
  ACTIVITY_METRICS,
  ACTIVITY_PERIODS,
  type ActivityPeriod,
} from '@/api/map/memberActivityMatrix'
import {
  betListSportLabel,
  memberBetProfitLiability,
  sportBookBetEventLabel,
  sportBookBetListTitle,
  type MemberBetTime,
  type MemberBetType,
  type MemberSportBookBet,
} from '@/api/map/memberBets'
import {
  LAB_TRANSFER_MAX_DAYS,
  labTransferNetShare,
  labTransferPayerPayee,
  labTransferRangeDays,
  labTransferSignedAmount,
  type CmTransferRow,
} from '@/api/map/memberTransfers'
import { Amount, Money } from '@/app/bits'
import { EMPTY, lll } from '@/lib/format'
import { formatLineSessionDisplayRun } from '@/lib/lineSessionDisplayRun'
import {
  balanceLinesFromAccount,
  limitUsage,
  round2,
} from '@/features/member/memberMoney'
import { BetListMemberNamePicker } from '@/features/member/BetListMemberNamePicker'
import { betListMatchedUnmatchedAmt } from '@/features/net-exposure/betListMatchedAmt'
import type { MemberAccount } from '@/features/member/memberAccount'
import {
  formatMoney,
  fromMinorUnits,
  parseCurrencyType,
} from '@/features/hierarchy/hierarchy'

/** Bet List Odds Placed — line ladder uses Back/Lay Run from `runs`. */
export function memberBetListOddsPlaced(
  b: Pick<MemberSportBookBet, 'isLineLadder' | 'runs' | 'side' | 'oddsPlaced'>,
): string {
  if (b.isLineLadder) {
    return b.runs != null ? formatLineSessionDisplayRun(b.runs, b.side) || EMPTY : EMPTY
  }
  return b.oddsPlaced ? b.oddsPlaced.toFixed(2) : EMPTY
}

function isoDayLocal(d = new Date()): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Agent Activity matrix parity (scope + date windows). Games/Sub/Type selects
 * omitted until analytics memberDetail / member-pnl accept product filters —
 * dead controls that only bust react-query cache are not kept.
 */
export function LabActivityTab({ account }: { account: MemberAccount }) {
  const today = isoDayLocal()
  const [memberScope, setMemberScope] = useState<'agent' | 'all-members'>('agent')
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [applied, setApplied] = useState({
    memberScope: 'agent' as 'agent' | 'all-members',
    fromDate: today,
    toDate: today,
  })
  const [searched, setSearched] = useState(true)

  const matrixQ = useMemberActivityMatrix(
    {
      memberId: account.id,
      memberScope: applied.memberScope,
      fromDate: applied.fromDate,
      toDate: applied.toDate,
    },
    searched,
  )

  const matrix = matrixQ.data
  const ccy = parseCurrencyType(account.currencyType)
  const customLabel = `${applied.fromDate} – ${applied.toDate}`

  type MatrixRow = { id: string; label: string; values: Record<ActivityPeriod, number> }
  const rows: MatrixRow[] = useMemo(() => {
    if (!matrix) return []
    return ACTIVITY_METRICS.map((m) => ({
      id: m.id,
      label: m.label,
      values: matrix.values[m.id],
    }))
  }, [matrix])

  const columns: Column<MatrixRow>[] = useMemo(
    () => [
      {
        id: 'metric',
        header: '',
        cell: (row) => <b>{row.label}</b>,
        sortValue: (row) => row.label,
        width: 140,
      },
      ...ACTIVITY_PERIODS.map(
        (p): Column<MatrixRow> => ({
          id: p.id,
          header: p.label,
          numeric: true,
          cell: (row) => <Amount value={row.values[p.id]} currency={account.currencyType} />,
          sortValue: (row) => row.values[p.id],
        }),
      ),
    ],
    [account.currencyType],
  )

  const markDirty = () => setSearched(false)

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
      <div className="w7-filterbar" role="region" aria-label="Activity filters">
        {account.role !== 'player' ? (
          <div className="w7-filterbar-row">
            <Segmented
              label="Member scope"
              value={memberScope}
              onChange={(v) => {
                setMemberScope(v)
                markDirty()
              }}
              items={[
                { id: 'agent', label: 'Agent' },
                { id: 'all-members', label: 'All Members' },
              ]}
            />
          </div>
        ) : null}
        <div className="w7-filterbar-row" style={{ alignItems: 'flex-end' }}>
          <Field label="From Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value)
                  markDirty()
                }}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value)
                  markDirty()
                }}
              />
            )}
          </Field>
          <Button
            variant="primary"
            busy={matrixQ.isFetching}
            onClick={() => {
              setApplied({
                memberScope,
                fromDate,
                toDate,
              })
              setSearched(true)
            }}
          >
            Search
          </Button>
        </div>
      </div>

      <Card
        flush
        title="Activity"
        subtitle={`${account.username} · ${applied.memberScope === 'agent' ? 'Agent' : 'All Members'} · ${customLabel}`}
      >
        {matrixQ.isFetching ? (
          <div
            className="w7-col"
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              minHeight: 220,
              padding: 'var(--w7-s5)',
            }}
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <Spinner size={22} />
            <p className="w7-dim" style={{ margin: 0 }}>
              Loading activity…
            </p>
          </div>
        ) : (
          <DataTable
            label="Activity matrix"
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            empty={
              <StateBlock icon="activity" title="No activity figures">
                Win, commission, P&amp;L and turnover for {account.username} would fill this sheet.
              </StateBlock>
            }
          />
        )}
      </Card>
      <p className="w7-dim" style={{ margin: 0, fontSize: 12 }}>
        Figures in {formatMoney(0, ccy).replace(/[\d.,]+/, '…')} for {account.username} only — not
        the signed-in seat.
      </p>
    </div>
  )
}

/**
 * Lab Detail Balance (Agent BalanceTab parity): 4 KPI strip + balance sheet
 * DataTable. Money + product NE from hierarchy agent (refetch on open).
 */
export function BalanceTab({ account }: { account: MemberAccount }) {
  const live = useQuery({
    queryKey: ['cm', 'hierarchy', 'agent', account.id, 'balance-tab'],
    queryFn: () => cmApi.hierarchyAgent(authHeaders(), account.id),
    staleTime: 15_000,
  })
  const node = live.data?.node
  const money: MemberAccount = node
    ? {
        ...account,
        creditLimit: fromMinorUnits(node.creditLimit),
        balance: fromMinorUnits(node.liveBalance ?? node.balance),
        distributedBalance: fromMinorUnits(node.distributedBalance),
        exposure: fromMinorUnits(node.exposure),
        exposureSportsbook:
          node.exposureSportsbook !== undefined
            ? fromMinorUnits(node.exposureSportsbook)
            : undefined,
        exposureLiveCasino:
          node.exposureLiveCasino !== undefined
            ? fromMinorUnits(node.exposureLiveCasino)
            : undefined,
        exposureLiveCards:
          node.exposureLiveCards !== undefined
            ? fromMinorUnits(node.exposureLiveCards)
            : undefined,
        exposurePremiumBm:
          node.exposurePremiumBm !== undefined
            ? fromMinorUnits(node.exposurePremiumBm)
            : undefined,
        exposureHorseRacing:
          node.exposureHorseRacing !== undefined
            ? fromMinorUnits(node.exposureHorseRacing)
            : undefined,
        settlement: fromMinorUnits(node.postedPnl),
        currencyType: node.currencyType ?? account.currencyType,
      }
    : account

  const ccy = parseCurrencyType(money.currencyType)
  const usage = limitUsage(money.exposure, money.creditLimit)
  const lines = balanceLinesFromAccount(money)

  type Row = (typeof lines)[number]
  const columns: Column<Row>[] = [
    {
      id: 'label',
      header: 'Item',
      cell: (row) => row.label,
      sortValue: (row) => row.label,
    },
    {
      id: 'value',
      header: 'Amount',
      numeric: true,
      cell: (row) => <Amount value={row.value} currency={money.currencyType} />,
      sortValue: (row) => row.value,
    },
  ]

  return (
    <div className="w7-col cm-member-balance" style={{ gap: 'var(--w7-s5)' }}>
      <div className="w7-grid w7-grid-4">
        <Kpi
          label="Live balance"
          value={formatMoney(money.balance, ccy)}
          tone="neutral"
        />
        <Kpi
          label="Credit limit"
          value={formatMoney(money.creditLimit, ccy)}
          tone="neutral"
        />
        <Kpi
          label="Open exposure"
          value={formatMoney(money.exposure, ccy)}
          tone={usage > 1 ? 'loss' : usage > 0.8 ? 'warn' : 'neutral'}
        />
        <Kpi
          label="Settlement"
          value={
            <Money value={round2(money.settlement)} signed currency={money.currencyType} />
          }
          tone={
            money.settlement > 0 ? 'profit' : money.settlement < 0 ? 'loss' : 'neutral'
          }
        />
      </div>
      <Card
        flush
        title="Balance"
        subtitle={`${money.username} · Lab Detail balance sheet`}
      >
        <DataTable
          label="Balance sheet"
          rows={lines}
          columns={columns}
          rowKey={(row) => row.id}
          loading={live.isLoading && !node}
        />
      </Card>
    </div>
  )
}

type BetListDraft = {
  fromDate: string
  toDate: string
  memberUsername: string | null
  highlightIp: string
  gameId: string
  subGame: string
  eventName: string
  marketName: string
  stakeFrom: string
  stakeTo: string
  time: MemberBetTime
  betType: MemberBetType
}

function initialBetListDraft(today: string): BetListDraft {
  return {
    fromDate: today,
    toDate: today,
    memberUsername: null,
    highlightIp: '',
    gameId: 'All',
    subGame: 'All',
    eventName: 'All',
    marketName: 'All',
    stakeFrom: '',
    stakeTo: '',
    time: 'Current',
    betType: 'Matched',
  }
}

/** Active filters tucked under More (dates + member stay on the bar). */
function betListMoreFilterCount(d: BetListDraft): number {
  let n = 0
  if (d.highlightIp.trim()) n += 1
  if (d.gameId !== 'All' && d.gameId !== 'all') n += 1
  if (d.subGame !== 'All') n += 1
  if (d.eventName !== 'All' && d.eventName !== '') n += 1
  if (d.marketName !== 'All' && d.marketName !== '') n += 1
  if (d.stakeFrom.trim()) n += 1
  if (d.stakeTo.trim()) n += 1
  return n
}

/** Agent Portal Bet List parity — FilterBar + More (Game / Sub Game / Event / …). */
export function BetListTab({ account }: { account: MemberAccount }) {
  const today = isoDayLocal()
  const pageSize = DEFAULT_PAGE_SIZE
  const [page, setPage] = useState(1)
  const [draft, setDraft] = useState<BetListDraft>(() => initialBetListDraft(today))
  const [applied, setApplied] = useState(draft)
  const [moreOpen, setMoreOpen] = useState(false)
  const moreAnchor = useRef<HTMLButtonElement>(null)

  const games = useGames()
  const draftGameId =
    draft.gameId === 'All' || draft.gameId === 'all' ? null : draft.gameId
  const appliedGameId =
    applied.gameId === 'All' || applied.gameId === 'all' ? null : applied.gameId
  const subGames = useSubGames(draftGameId)
  const eventsQuery = {
    range: { from: draft.fromDate, to: draft.toDate },
    ...(draftGameId ? { game: draftGameId } : {}),
    ...(draft.subGame && draft.subGame !== 'All' ? { subGame: draft.subGame } : {}),
    name: { _id: account.id },
  }
  const { data: pllEvents = [], isLoading: eventsLoading } = useEventsForPll(eventsQuery)

  const stakeFromNum = applied.stakeFrom.trim() === '' ? undefined : Number(applied.stakeFrom)
  const stakeToNum = applied.stakeTo.trim() === '' ? undefined : Number(applied.stakeTo)

  const { data, isLoading } = useMemberBets({
    accountId: account.id,
    accountRole: account.role,
    source: 'sportsbook',
    fromDate: applied.fromDate,
    toDate: applied.toDate,
    time: applied.time,
    betType: applied.betType,
    page,
    pageSize,
    game: appliedGameId ?? undefined,
    subGame: applied.subGame,
    eventName: applied.eventName,
    marketName: applied.marketName,
    stakeFrom:
      stakeFromNum != null && Number.isFinite(stakeFromNum) ? stakeFromNum : undefined,
    stakeTo: stakeToNum != null && Number.isFinite(stakeToNum) ? stakeToNum : undefined,
    memberUsername: applied.memberUsername,
  })

  const rows = data?.rows ?? []
  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize))
  const highlightIp = applied.highlightIp.trim()

  const applyFilters = (partial: Partial<BetListDraft>) => {
    setDraft((d) => ({ ...d, ...partial }))
    setApplied((a) => ({ ...a, ...partial }))
    setPage(1)
  }

  const clearAllExtraFilters = () => {
    applyFilters({
      memberUsername: null,
      highlightIp: '',
      gameId: 'All',
      subGame: 'All',
      eventName: 'All',
      marketName: 'All',
      stakeFrom: '',
      stakeTo: '',
    })
  }

  const sportsBookGames = (games.data ?? []).filter((g) => g.name === 'SportsBook')
  const gameOptions = [
    { value: 'All', label: 'All' },
    ...sportsBookGames.map((g) => ({ value: g._id, label: g.name })),
  ]
  const subOptions = [
    { value: 'All', label: 'All' },
    ...(subGames.data ?? []).map((name) => ({ value: name, label: name })),
  ]
  const eventOptions = [
    { value: 'All', label: 'All' },
    ...pllEvents.map((name) => ({ value: name, label: name })),
  ]
  const showEventPicker =
    draft.gameId !== 'All' && draft.gameId !== 'all' && draft.subGame !== 'All'
  const showMarketPicker = draft.eventName !== 'All' && draft.eventName !== ''
  const moreCount = betListMoreFilterCount(draft)

  const activeFilters = useMemo((): ActiveFilter[] => {
    const chips: ActiveFilter[] = []
    if (draft.memberUsername) {
      chips.push({
        id: 'member',
        label: `Member: ${draft.memberUsername}`,
        removeLabel: 'Clear member name filter',
        onRemove: () => applyFilters({ memberUsername: null }),
      })
    }
    if (draft.gameId !== 'All' && draft.gameId !== 'all') {
      const gameLabel =
        gameOptions.find((g) => g.value === draft.gameId)?.label ?? draft.gameId
      const sport = betListSportLabel(draft.subGame)
      // Sheet #46: sport next to SportsBook on the active filter chip.
      const label =
        sport && /^sports?book$/i.test(gameLabel)
          ? `Game: ${gameLabel} - ${sport}`
          : `Game: ${gameLabel}`
      chips.push({
        id: 'game',
        label,
        removeLabel: 'Clear game filter',
        onRemove: () =>
          applyFilters({
            gameId: 'All',
            subGame: 'All',
            eventName: 'All',
            marketName: 'All',
          }),
      })
    }
    if (draft.subGame !== 'All') {
      const gameLabel =
        gameOptions.find((g) => g.value === draft.gameId)?.label ?? ''
      // When already merged into SportsBook chip, skip duplicate Sub Game chip.
      if (!(betListSportLabel(draft.subGame) && /^sports?book$/i.test(gameLabel))) {
        chips.push({
          id: 'subGame',
          label: `Sub Game: ${draft.subGame}`,
          removeLabel: 'Clear sub game filter',
          onRemove: () =>
            applyFilters({ subGame: 'All', eventName: 'All', marketName: 'All' }),
        })
      }
    }
    if (draft.eventName !== 'All' && draft.eventName !== '') {
      chips.push({
        id: 'event',
        label: `Event: ${draft.eventName}`,
        removeLabel: 'Clear event filter',
        onRemove: () => applyFilters({ eventName: 'All', marketName: 'All' }),
      })
    }
    if (draft.marketName !== 'All' && draft.marketName !== '') {
      chips.push({
        id: 'market',
        label: `Market: ${draft.marketName}`,
        removeLabel: 'Clear market filter',
        onRemove: () => applyFilters({ marketName: 'All' }),
      })
    }
    if (draft.stakeFrom.trim()) {
      chips.push({
        id: 'stakeFrom',
        label: `Stakes from: ${draft.stakeFrom}`,
        removeLabel: 'Clear stakes from filter',
        onRemove: () => applyFilters({ stakeFrom: '' }),
      })
    }
    if (draft.stakeTo.trim()) {
      chips.push({
        id: 'stakeTo',
        label: `Stakes to: ${draft.stakeTo}`,
        removeLabel: 'Clear stakes to filter',
        onRemove: () => applyFilters({ stakeTo: '' }),
      })
    }
    if (draft.highlightIp.trim()) {
      chips.push({
        id: 'highlightIp',
        label: `Highlight IP: ${draft.highlightIp.trim()}`,
        removeLabel: 'Clear highlight IP',
        onRemove: () => applyFilters({ highlightIp: '' }),
      })
    }
    return chips
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draft.memberUsername,
    draft.gameId,
    draft.subGame,
    draft.eventName,
    draft.marketName,
    draft.stakeFrom,
    draft.stakeTo,
    draft.highlightIp,
    games.data,
  ])

  const columns: Column<MemberSportBookBet>[] = [
    {
      id: 'n',
      header: '#',
      width: 48,
      cell: (_b, i) => (page - 1) * pageSize + i + 1,
    },
    {
      id: 'member',
      header: 'Member',
      cell: (b) => b.userName || EMPTY,
      sortValue: (b) => b.userName,
    },
    {
      id: 'placed',
      header: 'Placed',
      width: 165,
      cell: (b) => (b.placedAt ? lll(b.placedAt) : EMPTY),
      sortValue: (b) => b.placedAt ?? '',
    },
    {
      id: 'matched',
      header: 'Matched Date',
      width: 165,
      cell: (b) => (b.matchedAt ? lll(b.matchedAt) : EMPTY),
      sortValue: (b) => b.matchedAt ?? '',
    },
    {
      id: 'event',
      header: 'Event',
      cell: (b) => sportBookBetEventLabel(b.eventType, b.eventName) || EMPTY,
      sortValue: (b) => sportBookBetEventLabel(b.eventType, b.eventName) || b.eventName,
    },
    {
      id: 'market',
      header: 'Market',
      cell: (b) => (
        <>
          {b.marketName === 'Multi Bet' ? 'Genie Combo Special' : b.marketName}
          {b.isBookmaker ? ' - Bookmaker' : ''}
        </>
      ),
      sortValue: (b) => b.marketName,
    },
    {
      id: 'selection',
      header: 'Selection',
      cell: (b) => (
        <span className="w7-row" style={{ gap: 6, alignItems: 'center' }}>
          <Chip tone={b.side === 'back' ? 'back' : 'lay'} size="sm">
            {b.side === 'back' ? 'Back' : 'Lay'}
          </Chip>
          {b.selection}
        </span>
      ),
      sortValue: (b) => b.selection,
    },
    {
      id: 'oddsPlaced',
      header: 'Odds Placed',
      numeric: true,
      cell: (b) => memberBetListOddsPlaced(b),
      sortValue: (b) => (b.isLineLadder ? (b.runs ?? 0) : b.oddsPlaced),
    },
    {
      id: 'oddsMatched',
      header: 'Odds Matched',
      numeric: true,
      cell: (b) => (b.oddsMatched ? b.oddsMatched.toFixed(3) : EMPTY),
      sortValue: (b) => b.oddsMatched,
    },
    {
      id: 'matchedAmt',
      header: 'Matched',
      numeric: true,
      cell: (b) => betListMatchedUnmatchedAmt(b.matched, b.currencyType, b.marketName),
      sortValue: (b) => b.matched,
    },
    {
      id: 'unmatchedAmt',
      header: 'Unmatched',
      numeric: true,
      cell: (b) => betListMatchedUnmatchedAmt(b.unmatched, b.currencyType, b.marketName),
      sortValue: (b) => b.unmatched,
    },
    {
      id: 'pl',
      header: 'Profit/Liability',
      numeric: true,
      cell: (b) => {
        const pl = memberBetProfitLiability(b, applied.time)
        const lost = applied.time === 'Past' && b.betWinStatus.toUpperCase() === 'LOST'
        const layOpen = applied.time !== 'Past' && b.side === 'lay'
        return (
          <span className={lost || layOpen ? 'w7-neg' : 'w7-pos'}>
            <Amount value={pl} currency={b.currencyType} />
          </span>
        )
      },
      sortValue: (b) => memberBetProfitLiability(b, applied.time),
    },
    ...(applied.time === 'Past'
      ? [
          {
            id: 'status',
            header: 'Status',
            cell: (b: MemberSportBookBet) => b.betWinStatus || b.status || EMPTY,
            sortValue: (b: MemberSportBookBet) => b.betWinStatus || b.status,
          } satisfies Column<MemberSportBookBet>,
        ]
      : []),
    {
      id: 'ip',
      header: 'IP',
      cell: (b) => (
        <span
          style={
            highlightIp && b.ip && b.ip === highlightIp
              ? { color: 'var(--w7-warn, #f0ad4e)' }
              : undefined
          }
        >
          {b.ip || EMPTY}
        </span>
      ),
      sortValue: (b) => b.ip,
    },
  ]

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
      <FilterBar
        label="SportBook bet list filters"
        active={activeFilters}
        onClearAll={activeFilters.length > 0 ? clearAllExtraFilters : undefined}
      >
        <div
          className="w7-col"
          style={{ flex: '1 1 100%', minWidth: 0, gap: 'var(--w7-s3)' }}
        >
          <div className="w7-filterbar-row" style={{ alignItems: 'flex-end' }}>
            <Field label="From Date">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={draft.fromDate}
                  onChange={(e) => applyFilters({ fromDate: e.target.value })}
                />
              )}
            </Field>
            <Field label="To Date">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={draft.toDate}
                  onChange={(e) => applyFilters({ toDate: e.target.value })}
                />
              )}
            </Field>
            <BetListMemberNamePicker
              rootId={account.id}
              value={draft.memberUsername}
              onChange={(username) => applyFilters({ memberUsername: username })}
            />
                <Button
                  ref={moreAnchor}
                  variant={moreCount > 0 || moreOpen ? 'secondary' : 'ghost'}
                  icon="filter"
                  aria-expanded={moreOpen}
                  aria-haspopup="dialog"
                  onClick={() => setMoreOpen((v) => !v)}
                >
                  {moreCount > 0 ? `More (${moreCount})` : 'More'}
                </Button>
                <Popover
                  open={moreOpen}
                  onClose={() => setMoreOpen(false)}
                  anchorRef={moreAnchor}
                  label="More filters"
                  align="end"
                  width={340}
                >
                  <div className="w7-betlist-more">
                    <label className="w7-betlist-more-field">
                      <span>Select Games</span>
                      <Select
                        aria-label="Select Games"
                        value={draft.gameId}
                        onChange={(e) =>
                          applyFilters({
                            gameId: e.target.value,
                            subGame: 'All',
                            eventName: 'All',
                            marketName: 'All',
                          })
                        }
                        options={gameOptions}
                      />
                    </label>
                    <label className="w7-betlist-more-field">
                      <span>Select Sub Games</span>
                      <Select
                        aria-label="Select Sub Games"
                        value={draft.subGame}
                        onChange={(e) =>
                          applyFilters({
                            subGame: e.target.value,
                            eventName: 'All',
                            marketName: 'All',
                          })
                        }
                        options={subOptions}
                      />
                    </label>
                    {showEventPicker ? (
                      <label className="w7-betlist-more-field">
                        <span>Select Event</span>
                        <Select
                          aria-label="Select Event"
                          value={draft.eventName}
                          disabled={eventsLoading}
                          onChange={(e) =>
                            applyFilters({
                              eventName: e.target.value || 'All',
                              marketName: 'All',
                            })
                          }
                          options={eventOptions}
                        />
                      </label>
                    ) : null}
                    {showMarketPicker ? (
                      <label className="w7-betlist-more-field">
                        <span>Select Market Type</span>
                        <Input
                          aria-label="Select Market Type"
                          placeholder="Select or search a market"
                          value={draft.marketName === 'All' ? '' : draft.marketName}
                          onChange={(e) =>
                            applyFilters({ marketName: e.target.value.trim() || 'All' })
                          }
                        />
                      </label>
                    ) : null}
                    <div className="w7-betlist-more-row">
                      <label className="w7-betlist-more-field">
                        <span>Stakes From</span>
                        <Input
                          type="number"
                          min={0}
                          aria-label="Stakes From"
                          placeholder="Stakes From Value"
                          value={draft.stakeFrom}
                          onChange={(e) => applyFilters({ stakeFrom: e.target.value })}
                        />
                      </label>
                      <label className="w7-betlist-more-field">
                        <span>Stakes To</span>
                        <Input
                          type="number"
                          min={0}
                          aria-label="Stakes To"
                          placeholder="Stakes To Value"
                          value={draft.stakeTo}
                          onChange={(e) => applyFilters({ stakeTo: e.target.value })}
                        />
                      </label>
                    </div>
                    <label className="w7-betlist-more-field">
                      <span>Highlight Ip</span>
                      <Input
                        aria-label="Highlight Ip"
                        placeholder="search for ip"
                        value={draft.highlightIp}
                        onChange={(e) => {
                          setDraft((d) => ({ ...d, highlightIp: e.target.value }))
                          setApplied((a) => ({ ...a, highlightIp: e.target.value }))
                        }}
                      />
                    </label>
                  </div>
                </Popover>
          </div>
          <div className="w7-col" style={{ gap: 'var(--w7-s2)', alignItems: 'flex-start' }}>
            <div className="w7-filterbar-row">
              <Segmented
                label="Bet list status"
                value={draft.time}
                onChange={(v) => {
                  const next: BetListDraft = {
                    ...draft,
                    time: v,
                    betType: v === 'Past' ? 'Matched' : draft.betType,
                  }
                  setDraft(next)
                  setApplied(next)
                  setPage(1)
                }}
                items={[
                  { id: 'Current', label: 'Current' },
                  { id: 'Past', label: 'Past' },
                ]}
              />
            </div>
            {draft.time === 'Current' ? (
              <div className="w7-filterbar-row">
                <Segmented
                  label="Match state"
                  value={draft.betType}
                  onChange={(v) => {
                    const next = { ...draft, betType: v }
                    setDraft(next)
                    setApplied(next)
                    setPage(1)
                  }}
                  items={[
                    { id: 'Matched', label: 'Matched' },
                    { id: 'Unmatched', label: 'Unmatched' },
                  ]}
                />
              </div>
            ) : null}
          </div>
        </div>
      </FilterBar>

      <Card
        flush
        title={sportBookBetListTitle(applied.subGame)}
        subtitle={`${account.username} · ${applied.fromDate} – ${applied.toDate} · ${applied.time}${
          applied.time === 'Current' ? ` · ${applied.betType}` : ''
        }`}
        footer={
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            total={data?.total ?? 0}
            pageSize={pageSize}
          />
        }
      >
        <DataTable
          label="SportBook bets"
          rows={rows}
          columns={columns}
          rowKey={(b) => b.id}
          loading={isLoading}
          skeletonRows={8}
          empty={
            <StateBlock icon="sport" title="No data found">
              No bets for {account.username} between {applied.fromDate} and {applied.toDate}.
            </StateBlock>
          }
        />
      </Card>
    </div>
  )
}

/**
 * Lab member-transfer-New parity (Agent TransfersTab): From/To + Search,
 * Total Net Share, Date / Payer-Payee / Amount. Data via CM-API
 * `POST /v1/agents/transfers` → analytics-service.
 */
export function TransfersTab({ account }: { account: MemberAccount }) {
  const toast = useToast()
  const today = isoDayLocal()
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [applied, setApplied] = useState({ fromDate: today, toDate: today })
  const [page, setPage] = useState(1)
  const pageSize = DEFAULT_PAGE_SIZE

  const rangeLabel = `${applied.fromDate} – ${applied.toDate}`
  const { data, isLoading, isFetching } = useMemberTransfers({
    agentId: account.id,
    from: applied.fromDate,
    to: applied.toDate,
    page,
    pageSize,
    currencyType: account.currencyType,
    partyOnly: true,
  })

  const rows = data?.rows ?? []
  const totals = data?.totals
  const netShare = totals ? labTransferNetShare(totals.given, totals.taken) : 0
  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize))

  const columns: Column<CmTransferRow>[] = useMemo(
    () => [
      {
        id: 'at',
        header: 'Date',
        width: 165,
        cell: (t) => lll(t.at),
        sortValue: (t) => t.at,
      },
      {
        id: 'payerPayee',
        header: 'Payer/Payee',
        cell: (t) => labTransferPayerPayee(t.from?.username, t.to?.username),
        sortValue: (t) => labTransferPayerPayee(t.from?.username, t.to?.username),
      },
      {
        id: 'amount',
        header: 'Amount',
        numeric: true,
        cell: (t) => (
          <Money
            value={labTransferSignedAmount(t.amount, t.to?.username, account.username)}
            currency={account.currencyType}
          />
        ),
        sortValue: (t) => labTransferSignedAmount(t.amount, t.to?.username, account.username),
      },
    ],
    [account.username, account.currencyType],
  )

  const runSearch = () => {
    const days = labTransferRangeDays(fromDate, toDate)
    if (days > LAB_TRANSFER_MAX_DAYS) {
      toast.error(
        'Date range too wide',
        `The date you have selected exceeds ${LAB_TRANSFER_MAX_DAYS} days. Please try again.`,
      )
      return
    }
    setPage(1)
    setApplied({ fromDate, toDate })
  }

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
      <div className="w7-filterbar" role="region" aria-label="Transfer statement filters">
        <div className="w7-transfer-filter-row">
          <Field label="From Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id }) => (
              <Input id={id} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            )}
          </Field>
          <Button type="button" variant="primary" busy={isFetching} onClick={runSearch}>
            Search
          </Button>
          <div className="w7-transfer-net-share">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--w7-s3)' }}>
              <span className="w7-muted" style={{ fontSize: 'var(--w7-fs-sm)' }}>
                Total Net Share
              </span>
              <Money value={netShare} currency={account.currencyType} />
            </div>
            <Legend
              items={[
                { label: 'Player Loss', color: 'var(--w7-loss)' },
                { label: 'Player Win', color: 'var(--w7-profit)' },
              ]}
            />
          </div>
        </div>
      </div>

      <Card
        flush
        title="Transfer Statements"
        subtitle={`${account.username} · ${rangeLabel}`}
        footer={
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            total={data?.total ?? 0}
            pageSize={pageSize}
          />
        }
      >
        <DataTable
          label="Transfer statements"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={isLoading}
          skeletonRows={8}
          empty={
            <StateBlock icon="transfers" title="No data found">
              No credit transfers for {account.username} between {rangeLabel}.
            </StateBlock>
          }
        />
      </Card>
    </div>
  )
}

/**
 * Agent-console Login History parity: From/To + Search, Block / Remove Blocked
 * Device Id, table (# Name Date IP Device Browser OS ISP City) + Pager + IP modal.
 * Data: CM-API `POST /v1/userLog/getuserLog` → hierarchy.
 */
export function LoginHistoryTab({ account }: { account: MemberAccount }) {
  const toast = useToast()
  const yesterday = (() => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    return isoDayLocal(d)
  })()
  const today = isoDayLocal()
  const [fromDate, setFromDate] = useState(yesterday)
  const [toDate, setToDate] = useState(today)
  const [applied, setApplied] = useState({ fromDate: yesterday, toDate: today })
  const [searchNonce, setSearchNonce] = useState(0)
  const [page, setPage] = useState(1)
  const pageSize = DEFAULT_PAGE_SIZE
  const [blockOpen, setBlockOpen] = useState(false)
  const [blockComment, setBlockComment] = useState('')
  const [removeOpen, setRemoveOpen] = useState(false)
  const [ipRow, setIpRow] = useState<LoginHistoryRow | null>(null)

  const { data, isLoading, isFetching, isError, error } = useLoginHistory({
    memberId: account.id,
    fromDate: applied.fromDate,
    toDate: applied.toDate,
    page,
    pageSize,
    searchNonce,
  })
  const saveBlock = useSaveMemberBlockedDeviceId()
  const deleteBlock = useDeleteMemberBlockedDeviceId()

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const rangeLabel = `${applied.fromDate} – ${applied.toDate}`
  const loadError =
    isError && error instanceof Error ? error.message : isError ? 'Login history request failed' : null

  const columns: Column<LoginHistoryRow>[] = useMemo(
    () => [
      {
        id: 'n',
        header: '#',
        width: 48,
        cell: (_row, i) => (page - 1) * pageSize + i + 1,
      },
      {
        id: 'name',
        header: 'Name',
        cell: (row) => row.name || account.username,
        sortValue: (row) => row.name,
      },
      {
        id: 'date',
        header: 'Date',
        cell: (row) => lll(row.at),
        sortValue: (row) => row.at,
      },
      {
        id: 'ip',
        header: 'IP',
        cell: (row) =>
          row.ip ? (
            <button
              type="button"
              className="w7-linkish"
              onClick={() => setIpRow(row)}
              title="IP detail"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span aria-hidden="true" className="w7-muted">
                (i)
              </span>
              {row.ip}
            </button>
          ) : (
            EMPTY
          ),
        sortValue: (row) => row.ip,
      },
      {
        id: 'device',
        header: 'Device',
        cell: (row) => row.device || EMPTY,
        sortValue: (row) => row.device,
      },
      {
        id: 'browser',
        header: 'Browser',
        cell: (row) => row.browser || EMPTY,
        sortValue: (row) => row.browser,
      },
      {
        id: 'os',
        header: 'OS',
        cell: (row) => row.os || EMPTY,
        sortValue: (row) => row.os,
      },
      {
        id: 'isp',
        header: 'ISP',
        hideBelow: 1080,
        cell: (row) => row.isp || EMPTY,
        sortValue: (row) => row.isp,
      },
      {
        id: 'city',
        header: 'City',
        hideBelow: 900,
        cell: (row) => row.city || EMPTY,
        sortValue: (row) => row.city,
      },
    ],
    [account.username, page, pageSize],
  )

  const runSearch = () => {
    setPage(1)
    setApplied({ fromDate, toDate })
    // Same From/To as last apply → queryKey unchanged without nonce; always re-hit API.
    setSearchNonce((n) => n + 1)
  }

  const onBlock = async () => {
    try {
      const res = await saveBlock.mutateAsync({
        memberId: account.id,
        comment: blockComment,
      })
      toast.success(
        `${account.username}${res.data?.msg ?? ' Device ID blocked successfully.'}`,
      )
      setBlockOpen(false)
      setBlockComment('')
    } catch (err) {
      toast.error(
        err instanceof CmApiHttpError || err instanceof Error
          ? err.message
          : 'Unable to block device id',
      )
    }
  }

  const onRemove = async () => {
    try {
      await deleteBlock.mutateAsync({ memberId: account.id })
      toast.success(`${account.username} Blocked Device ID Removed successfully.`)
      setRemoveOpen(false)
    } catch (err) {
      toast.error(
        err instanceof CmApiHttpError || err instanceof Error
          ? err.message
          : 'Unable to remove blocked device id',
      )
    }
  }

  const q = ipRow?.qualityScore ?? {}
  const qStr = (key: string) => {
    const v = q[key]
    if (v == null || v === '') return EMPTY
    return String(v)
  }

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
      <div className="w7-filterbar" role="region" aria-label="Login history filters">
        <div className="w7-transfer-filter-row">
          <Field label="From Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id }) => (
              <Input id={id} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            )}
          </Field>
          <Button type="button" variant="primary" busy={isFetching} onClick={runSearch}>
            Search
          </Button>
          <Button type="button" variant="primary" onClick={() => setBlockOpen(true)}>
            Block Device Id
          </Button>
          <Button type="button" variant="primary" onClick={() => setRemoveOpen(true)}>
            Remove Blocked Device Id
          </Button>
        </div>
      </div>

      <Card
        flush
        title="Login History"
        subtitle={`${account.username} · ${rangeLabel}`}
        footer={
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={pageSize}
          />
        }
      >
        <DataTable
          label="Login history"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={isLoading || isFetching}
          skeletonRows={6}
          empty={
            loadError ? (
              <StateBlock icon="warning" title="Login history unavailable">
                {loadError}
              </StateBlock>
            ) : (
              <StateBlock icon="activity" title="No data found">
                No login history for {account.username} between {rangeLabel}.
              </StateBlock>
            )
          }
        />
      </Card>

      <Modal
        open={blockOpen}
        onClose={() => {
          if (!saveBlock.isPending) {
            setBlockOpen(false)
            setBlockComment('')
          }
        }}
        title="Are you sure you want to block Device ID"
        closeDisabled={saveBlock.isPending}
        footer={
          <>
            <span className="w7-spacer" />
            <Button
              variant="secondary"
              onClick={() => {
                setBlockOpen(false)
                setBlockComment('')
              }}
              disabled={saveBlock.isPending}
            >
              No
            </Button>
            <Button variant="primary" busy={saveBlock.isPending} onClick={() => void onBlock()}>
              Yes
            </Button>
          </>
        }
      >
        <Field label="Comments">
          {({ id }) => (
            <Textarea
              id={id}
              rows={3}
              placeholder="Enter Comments"
              value={blockComment}
              onChange={(e) => setBlockComment(e.target.value)}
            />
          )}
        </Field>
      </Modal>

      <Modal
        open={removeOpen}
        onClose={() => {
          if (!deleteBlock.isPending) setRemoveOpen(false)
        }}
        title="Are you sure you want to Remove Blocked Device ID"
        closeDisabled={deleteBlock.isPending}
        footer={
          <>
            <span className="w7-spacer" />
            <Button
              variant="secondary"
              onClick={() => setRemoveOpen(false)}
              disabled={deleteBlock.isPending}
            >
              No
            </Button>
            <Button
              variant="danger"
              busy={deleteBlock.isPending}
              onClick={() => void onRemove()}
            >
              Yes
            </Button>
          </>
        }
      >
        <p className="w7-muted" style={{ margin: 0 }}>
          This removes blocked device ids linked to {account.username}.
        </p>
      </Modal>

      <Modal
        open={ipRow !== null}
        onClose={() => setIpRow(null)}
        title="Ip Detail"
        footer={
          <>
            <span className="w7-spacer" />
            <Button variant="secondary" onClick={() => setIpRow(null)}>
              Close
            </Button>
          </>
        }
      >
        {ipRow ? (
          <div className="w7-col" style={{ gap: 'var(--w7-s2)', fontSize: 'var(--w7-fs-sm)' }}>
            {(
              [
                ['ip', ipRow.ip],
                ['ISP', ipRow.isp || qStr('ISP')],
                ['city', ipRow.city || qStr('city')],
                ['country_code', qStr('country_code')],
                ['region', qStr('region')],
                ['organization', qStr('organization')],
                ['host', qStr('host')],
                ['fraud_score', qStr('fraud_score')],
                ['vpn', qStr('vpn')],
                ['proxy', qStr('proxy')],
                ['connection_type', qStr('connection_type')],
              ] as const
            ).map(([label, value]) => (
              <div
                key={label}
                style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8 }}
              >
                <span className="w7-muted">{label}</span>
                <span>{value || EMPTY}</span>
              </div>
            ))}
          </div>
        ) : null}
      </Modal>
    </div>
  )
}

/**
 * Lab `member-commission` parity (Backend-UI): From/To + Search, then Fancy and
 * Bookmaker tiles showing upline commission (debit-style when > 0).
 */
export function CommissionTab({ account }: { account: MemberAccount }) {
  const today = isoDayLocal()
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [applied, setApplied] = useState({ fromDate: today, toDate: today })
  const [searchNonce, setSearchNonce] = useState(0)

  const rangeLabel = `${applied.fromDate} – ${applied.toDate}`
  const fancyQ = useFancyBookmakerCommission(
    {
      memberId: account.id,
      from: applied.fromDate,
      to: applied.toDate,
      searchNonce,
    },
    true,
  )
  const fancyUpline = fancyQ.data?.fancy.upline ?? 0
  const bookmakerUpline = fancyQ.data?.bookmaker.upline ?? 0
  const ccy = parseCurrencyType(account.currencyType)

  const runSearch = () => {
    setApplied({ fromDate, toDate })
    // Same From/To as last apply → queryKey unchanged without nonce; always re-hit API.
    setSearchNonce((n) => n + 1)
  }

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
      <div className="w7-filterbar" role="region" aria-label="Commission filters">
        <div className="w7-filterbar-row" style={{ alignItems: 'flex-end' }}>
          <Field label="From Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id }) => (
              <Input id={id} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            )}
          </Field>
          <Button variant="primary" busy={fancyQ.isFetching} onClick={runSearch}>
            Search
          </Button>
        </div>
      </div>

      {fancyQ.isLoading ? (
        <Skeleton height={160} />
      ) : fancyQ.data?.unavailable ? (
        <StateBlock icon="warning" title="Commission unavailable">
          {fancyQ.data.reason || 'Could not load Fancy / Bookmaker commission.'}
        </StateBlock>
      ) : (
        <div className="w7-grid w7-grid-2">
          <Kpi
            label="Fancy"
            value={
              <Amount value={labMemberCommissionDisplay(fancyUpline)} currency={ccy} />
            }
            meta={`${account.username} · ${rangeLabel}`}
          />
          <Kpi
            label="Bookmaker"
            value={
              <Amount
                value={labMemberCommissionDisplay(bookmakerUpline)}
                currency={ccy}
              />
            }
            meta={`${account.username} · ${rangeLabel}`}
          />
        </div>
      )}
    </div>
  )
}
